import { liquityBranchesByChain, liquityConfigFor } from '@1delta/data-sdk'
import {
  LiquityActivePoolAbi,
  LiquityPriceFeedAbi,
  LiquitySortedTrovesAbi,
  LiquityStabilityPoolAbi,
  LiquityTroveManagerAbi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { LiquityBranchRaw, LiquityMarketsRaw } from './types'

// 8 reads per branch, in this order (keep in sync with the slicer below).
const READS_PER_BRANCH = 8

// One merged ABI for the whole read set — function names are unique across
// the five branch contracts, so name-based resolution is unambiguous while
// each call targets its own address (same trick as the user-data ABIs).
const LIQUITY_PUBLIC_READ_ABI = [
  ...LiquityTroveManagerAbi,
  ...LiquityActivePoolAbi,
  ...LiquityStabilityPoolAbi,
  ...LiquityPriceFeedAbi,
  ...LiquitySortedTrovesAbi,
] as any

/**
 * Fetch all branch data of ONE Liquity-family deployment — FULLY ON-CHAIN via
 * one retrying multicall (no API/indexer; the optional api.liquity.org stats
 * endpoint is display enrichment only and is NOT fetched here, so a dead API
 * can never break the data path). Branch addresses + fork deviation params
 * come from lender-metadata (`liquityConfig`/`liquityMarkets`, keyed
 * lender → chain). `multicallRetryUniversal` rotates RPCs on failure.
 *
 * Price: `lastGoodPrice` (view) — updated on every borrower op, so fresh on
 * any active branch. (`fetchPrice` would be exact but returns a tuple and is
 * nonpayable; revisit if quiet-fork staleness ever matters.)
 */
export async function fetchLiquityMarkets(
  lender: string,
  chainId: string,
): Promise<LiquityMarketsRaw> {
  const config = liquityConfigFor(lender, chainId)
  const branches = liquityBranchesByChain(lender, chainId)
  if (!config || branches.length === 0) {
    return { lender, config, branches: [] }
  }

  const calls = branches.flatMap((b) => [
    { address: b.troveManager, name: 'getEntireBranchDebt', params: [] },
    { address: b.troveManager, name: 'getEntireBranchColl', params: [] },
    { address: b.troveManager, name: 'shutdownTime', params: [] },
    { address: b.activePool, name: 'aggRecordedDebt', params: [] },
    { address: b.activePool, name: 'aggWeightedDebtSum', params: [] },
    { address: b.stabilityPool, name: 'getTotalBoldDeposits', params: [] },
    { address: b.priceFeed, name: 'lastGoodPrice', params: [] },
    { address: b.sortedTroves, name: 'getSize', params: [] },
  ])

  let results: any[] = []
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: LIQUITY_PUBLIC_READ_ABI,
      allowFailure: true,
    })) as any[]
  } catch {
    return { lender, config, branches: [] }
  }

  // Failed (allowFailure) slots arrive as '0x' — mapped to null.
  const val = (i: number): bigint | null => {
    const r = results[i]
    if (typeof r === 'bigint') return r
    if (typeof r === 'number') return BigInt(r)
    return null
  }

  const out: LiquityBranchRaw[] = branches.map((branch, i) => {
    const base = i * READS_PER_BRANCH
    return {
      branch,
      entireDebt: val(base),
      entireColl: val(base + 1),
      shutdownTime: val(base + 2),
      aggRecordedDebt: val(base + 3),
      aggWeightedDebtSum: val(base + 4),
      spDeposits: val(base + 5),
      collPrice: val(base + 6),
      troveCount: val(base + 7),
    }
  })

  return { lender, config, branches: out }
}
